import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { dismissPrompt, isPromptDismissed } from '../../utils/softUpgradePromptStorage';
import { trackProductEvent } from '../../utils/productEvents';

const PROMPT_LIMIT_FLAG_KEYS = {
  'people-growth': {
    near: ['people_near_limit'],
    over: ['people_over_limit'],
  },
  'pdf-export': {
    near: ['people_near_limit', 'memories_near_limit', 'photos_near_limit'],
    over: ['people_over_limit', 'memories_over_limit', 'photos_over_limit'],
  },
  'memorial-lifetime': {
    near: [],
    over: [],
  },
  'family-archive-voice': {
    near: ['voice_recordings_near_limit', 'transcription_minutes_near_limit', 'ai_polish_near_limit'],
    over: ['voice_recordings_over_limit', 'transcription_minutes_over_limit', 'ai_polish_over_limit'],
  },
};

function fireLimitEvents(flags, promptId, treeId) {
  const mapping = PROMPT_LIMIT_FLAG_KEYS[promptId];
  if (!mapping || !flags) return;
  const scopeMeta = { prompt_id: promptId, tree_id: treeId || undefined };
  if (mapping.near.some((key) => flags[key])) {
    trackProductEvent('limit_near', { pillar: 'billing', metadata: scopeMeta });
  }
  if (mapping.over.some((key) => flags[key])) {
    trackProductEvent('limit_over', { pillar: 'billing', metadata: scopeMeta });
  }
}

/**
 * Calm, dismissible upgrade guidance — observability only (Stage 4B.3).
 * Does not block any user action.
 */
export function SoftUpgradePrompt({
  promptId,
  treeId = null,
  visible = false,
  message,
  ctaLabel,
  ctaTo,
  featureName = null,
  snapshot = null,
  className = '',
  dismissible = true,
  'data-testid': testId,
}) {
  const [dismissed, setDismissed] = useState(() => isPromptDismissed(promptId, treeId));
  const telemetrySentRef = useRef(null);

  useEffect(() => {
    setDismissed(isPromptDismissed(promptId, treeId));
  }, [promptId, treeId]);

  useEffect(() => {
    if (!visible || dismissed) return;

    const contextKey = `${promptId}:${treeId || 'global'}:${snapshot?.plan_type || 'free'}`;
    if (telemetrySentRef.current === contextKey) return;
    telemetrySentRef.current = contextKey;

    const scopeMeta = { prompt_id: promptId, tree_id: treeId || undefined };
    trackProductEvent('upgrade_prompt_seen', { pillar: 'billing', metadata: scopeMeta });
    if (featureName) {
      trackProductEvent('paid_feature_previewed', {
        pillar: 'billing',
        metadata: { ...scopeMeta, feature: featureName },
      });
    }
    fireLimitEvents(snapshot?.soft_flags, promptId, treeId);
  }, [visible, dismissed, promptId, treeId, featureName, snapshot]);

  if (!visible || dismissed) return null;

  const handleDismiss = () => {
    dismissPrompt(promptId, treeId);
    setDismissed(true);
  };

  return (
    <div
      className={`relative rounded-xl border border-[#e8e2d8] bg-[#faf7f2] px-4 py-3 text-sm text-[#5c534a] ${className}`}
      data-testid={testId || `soft-upgrade-prompt-${promptId}`}
      role="status"
    >
      <p className="pr-8 leading-relaxed">{message}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button asChild variant="outline" size="sm" className="rounded-full border-[#c9a961]/40">
          <Link to={ctaTo}>{ctaLabel}</Link>
        </Button>
        {dismissible ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="rounded-full text-xs text-[#6c6258]"
            onClick={handleDismiss}
            data-testid={`${testId || `soft-upgrade-prompt-${promptId}`}-dismiss`}
          >
            Dismiss
          </Button>
        ) : null}
      </div>
      {dismissible ? (
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute right-2 top-2 rounded-md p-1 text-[#8b7355] hover:bg-[#e8e2d8]/60"
          aria-label="Dismiss upgrade guidance"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

export default SoftUpgradePrompt;
